// B3 (domain/organiserLock): the PIN pad in front of the grown-up screens on a grown-up's own phone
// after 10 minutes away. Cancel goes back to the Map; the PIN also counts as a recent PIN on the
// server (/api/session/elevate), so the big changes that ask for one don't ask twice.
import { useEffect, useSyncExternalStore } from 'react'
import { api } from '../data/api'
import { organiserTouch, useSession } from '../data/session'
import { isGrownUpScreen, organiserLocked } from '../domain/organiserLock'
import { pinProblem } from '../screens/WhoScreen'
import { t } from '../strings/en'
import { PinPad } from './PinPad'
import { currentRoute, goTab, onRouteChange } from './router'

// Decided outside React, whenever the route or who's signed in changes: on arriving at a grown-up
// screen, from the last touch. Using the screens keeps them open.
let locked = false
const subs = new Set<() => void>()
function decide() {
  const { person, returnTo } = useSession.getState()
  const r = currentRoute()
  const next = !!person && organiserLocked({ person, parentMode: !!returnTo, onGrownUpScreen: isGrownUpScreen(r.tab, r.overlay), lastTouch: organiserTouch.read(), now: Date.now() })
  if (next === locked) return
  locked = next
  subs.forEach((s) => s())
}

function subscribe(cb: () => void) {
  if (!subs.size) decide()
  subs.add(cb)
  const offRoute = onRouteChange(decide)
  const offSession = useSession.subscribe(decide)
  return () => {
    subs.delete(cb)
    offRoute()
    offSession()
  }
}

export function useOrganiserLock(tab: string, overlay: string | null | undefined): boolean {
  const isLocked = useSyncExternalStore(subscribe, () => locked)
  const onGrownUpScreen = isGrownUpScreen(tab, overlay)
  useEffect(() => {
    if (!onGrownUpScreen || isLocked) return
    const touch = () => organiserTouch.write(Date.now())
    touch()
    window.addEventListener('pointerdown', touch, { passive: true })
    window.addEventListener('keydown', touch)
    return () => {
      window.removeEventListener('pointerdown', touch)
      window.removeEventListener('keydown', touch)
    }
  }, [onGrownUpScreen, isLocked])
  return isLocked
}

function unlock() {
  organiserTouch.write(Date.now())
  decide()
}

export function OrganiserLock() {
  return (
    <main className="screen who pin-gate">
      <PinPad
        title={t.pin.lockTitle}
        sub={t.pin.lockSub}
        onCancel={() => goTab('map')}
        onSubmit={async (pin) => {
          try {
            await api('/api/session/elevate', { method: 'POST', body: JSON.stringify({ pin }) })
          } catch (e) {
            return pinProblem(e) ?? String(e)
          }
          unlock()
          return null
        }}
      />
    </main>
  )
}
